import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Minus, ArrowUp, ArrowDown, Users } from "lucide-react";
import { ProjectTask } from "../types";

interface TaskInsertionManagerProps {
  tasks: ProjectTask[];
  onTasksUpdate: (tasks: ProjectTask[]) => void;
  excludeWeekends: boolean;
  projectStartDate: string;
}

export const TaskInsertionManager: React.FC<TaskInsertionManagerProps> = ({
  tasks,
  onTasksUpdate,
  excludeWeekends,
  projectStartDate
}) => {
  const [isInsertDialogOpen, setIsInsertDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteAbortDialogOpen, setIsDeleteAbortDialogOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDuration, setNewTaskDuration] = useState<number>(1);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number | null>(null);
  const [taskNameError, setTaskNameError] = useState<string>('');
  const [durationError, setDurationError] = useState<string>('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const taskRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Helper function to check if task name is similar to existing tasks
  const checkTaskNameSimilarity = (inputName: string): boolean => {
    if (!inputName.trim()) return false;
    
    const normalizedInput = inputName.toLowerCase().trim();
    return tasks.some(task => 
      task.name.toLowerCase().trim() === normalizedInput
    );
  };

  // Handle task name change with validation
  const handleTaskNameChange = (value: string) => {
    setNewTaskName(value);
    
    if (value.trim() && checkTaskNameSimilarity(value)) {
      setTaskNameError('Provide a unique project task name.');
    } else {
      setTaskNameError('');
    }
  };

  // Handle duration change with validation
  const handleDurationChange = (value: string) => {
    const numericValue = parseInt(value) || 0;
    setNewTaskDuration(numericValue);
    
    if (numericValue < 1) {
      setDurationError('Provide a positive value.');
    } else {
      setDurationError('');
    }
  };

  // Simple and reliable scroll function to keep selected task visible
  const scrollToSelectedTask = (taskIndex: number) => {
    if (taskRefs.current[taskIndex] && scrollAreaRef.current) {
      const taskElement = taskRefs.current[taskIndex];
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      
      if (taskElement && scrollContainer) {
        // Simple scroll into view with center alignment
        taskElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }
  };

  // Update task refs when tasks change
  useEffect(() => {
    taskRefs.current = taskRefs.current.slice(0, tasks.length);
  }, [tasks.length]);

  // Helper function to calculate sequential dates
  const recalculateTaskDates = (tasksToUpdate: ProjectTask[]): ProjectTask[] => {
    const updatedTasks = [...tasksToUpdate];
    let currentDate = new Date(projectStartDate);
    for (let i = 0; i < updatedTasks.length; i++) {
      const task = updatedTasks[i];
      const startDate = new Date(currentDate);

      // Calculate end date based on duration and weekend exclusion
      let endDate = new Date(startDate);
      let daysToAdd = task.duration - 1; // Subtract 1 because start date counts as day 1

      if (excludeWeekends) {
        // Skip weekends when calculating end date
        while (daysToAdd > 0) {
          endDate.setDate(endDate.getDate() + 1);
          const dayOfWeek = endDate.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            // Not Sunday (0) or Saturday (6)
            daysToAdd--;
          }
        }
      } else {
        endDate.setDate(endDate.getDate() + daysToAdd);
      }

      // Update task dates
      updatedTasks[i] = {
        ...task,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };

      // Set next task start date (day after current task ends)
      currentDate = new Date(endDate);
      currentDate.setDate(currentDate.getDate() + 1);

      // Skip weekends for next task start if needed
      if (excludeWeekends) {
        while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    }
    return updatedTasks;
  };

  const handleInsertNewTask = () => {
    setNewTaskName('');
    setNewTaskDuration(1);
    setTaskNameError('');
    setDurationError('');
    setIsInsertDialogOpen(true);
  };

  const confirmInsertTask = () => {
    if (!newTaskName.trim() || taskNameError) return;
    
    const newTask: ProjectTask = {
      name: newTaskName.trim(),
      duration: newTaskDuration,
      progress: 0,
      phase: "Main Tasks",
      startDate: "",
      endDate: "",
      dependencies: "",
      isCritical: false,
      isMilestone: false
    };
    
    const updatedTasks = [newTask, ...tasks]; // Insert at beginning
    
    // Recalculate all dates after insertion
    const recalculatedTasks = recalculateTaskDates(updatedTasks);
    onTasksUpdate(recalculatedTasks);
    
    // Auto-select the new task (which is now at index 0)
    setSelectedTaskIndex(0);
    setIsInsertDialogOpen(false);
  };

  const handleDeleteTask = () => {
    if (selectedTaskIndex === null) return;
    
    // Check if deleting this task would leave the project with 0 tasks
    if (tasks.length === 1) {
      setIsDeleteAbortDialogOpen(true);
      return;
    }
    
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteTask = () => {
    if (selectedTaskIndex === null) return;
    
    const updatedTasks = [...tasks];
    updatedTasks.splice(selectedTaskIndex, 1);
    
    // Recalculate all dates after deletion
    const recalculatedTasks = recalculateTaskDates(updatedTasks);
    onTasksUpdate(recalculatedTasks);
    
    // Clear selection
    setSelectedTaskIndex(null);
    setIsDeleteDialogOpen(false);
  };

  const moveSelectedTask = (direction: 'up' | 'down') => {
    if (selectedTaskIndex === null) return;
    
    const newIndex = direction === 'up' 
      ? Math.max(0, selectedTaskIndex - 1)
      : Math.min(tasks.length - 1, selectedTaskIndex + 1);
    
    if (newIndex === selectedTaskIndex) return;

    const updatedTasks = [...tasks];
    const [movedTask] = updatedTasks.splice(selectedTaskIndex, 1);
    updatedTasks.splice(newIndex, 0, movedTask);

    // Recalculate all dates after resequencing
    const recalculatedTasks = recalculateTaskDates(updatedTasks);
    onTasksUpdate(recalculatedTasks);
    setSelectedTaskIndex(newIndex);

    // Scroll to keep selected task visible after a brief delay
    setTimeout(() => scrollToSelectedTask(newIndex), 100);
  };

  const handleTaskSelect = (index: number) => {
    setSelectedTaskIndex(selectedTaskIndex === index ? null : index);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Format as DD MMM YYYY (e.g., 23 Dec 2024)
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <>
      {/* Header with control badges - Fixed at top */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-white text-lg flex items-center gap-2 py-2">
            <Users className="h-5 w-5 text-primary" />
            Project Tasks Management
          </h3>
          <div className="flex items-center space-x-2">
            <Badge 
              variant="outline" 
              className="cursor-pointer bg-purple-500/20 text-purple-300 border-purple-500/30 hover:bg-purple-500/30 p-2"
              onClick={() => moveSelectedTask('up')}
              title="Move selected task up"
            >
              <ArrowUp className="h-4 w-4" />
            </Badge>
            <Badge 
              variant="outline" 
              className="cursor-pointer bg-blue-500/20 text-blue-300 border-blue-500/30 hover:bg-blue-500/30 p-2"
              onClick={() => moveSelectedTask('down')}
              title="Move selected task down"
            >
              <ArrowDown className="h-4 w-4" />
            </Badge>
            <Badge 
              variant="outline" 
              className="cursor-pointer bg-green-500/20 text-green-300 border-green-500/30 hover:bg-green-500/30 p-2"
              onClick={handleInsertNewTask}
              title="Insert new task"
            >
              <Plus className="h-4 w-4" />
            </Badge>
            <Badge 
              variant="outline" 
              className={`p-2 ${
                selectedTaskIndex !== null 
                  ? 'cursor-pointer bg-orange-500/20 text-orange-300 border-orange-500/30 hover:bg-orange-500/30' 
                  : 'cursor-not-allowed opacity-50 bg-gray-500/10 text-gray-400 border-gray-500/20'
              }`}
              onClick={selectedTaskIndex !== null ? handleDeleteTask : undefined}
              title={selectedTaskIndex !== null ? "Delete selected task" : "Select a task to delete"}
            >
              <Minus className="h-4 w-4" />
            </Badge>
          </div>
        </div>
      </div>

      {/* Scrollable task list - Matching Cost Analysis design */}
      <div className="h-[330px] overflow-y-auto border border-primary/20 rounded-lg p-2 bg-black/20" ref={scrollAreaRef}>
        <div className="space-y-1.5">
          {tasks.map((task, index) => {
            const colors = [
              { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-200', badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
              { bg: 'bg-pink-500/10', border: 'border-pink-500/20', text: 'text-pink-200', badge: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
              { bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', text: 'text-yellow-200', badge: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
              { bg: 'bg-indigo-500/10', border: 'border-indigo-500/20', text: 'text-indigo-200', badge: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
              { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-200', badge: 'bg-red-500/20 text-red-300 border-red-500/30' },
              { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-200', badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' }
            ];
            const color = colors[index % colors.length];
            
            return (
              <div 
                key={index} 
                ref={(el) => taskRefs.current[index] = el}
                onClick={() => handleTaskSelect(index)}
                className={`${color.bg} p-3 rounded-lg border ${color.border} cursor-pointer transition-colors ${
                  selectedTaskIndex === index ? 'ring-2 ring-primary/50' : 'hover:bg-opacity-80'
                }`}
              >
                {/* First row - Task number and full task name */}
                <div className="flex items-start gap-2 mb-2">
                  <span className={`${color.text} font-medium text-xs flex-shrink-0 mt-0.5`}>{index + 1}</span>
                  <span className={`${color.text} font-medium text-xs leading-tight`}>{task.name}</span>
                </div>
                
                {/* Second row - Dates and duration */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {task.startDate && task.endDate ? (
                      <div className="text-white text-xs font-semibold">
                        {formatDate(task.startDate)} - {formatDate(task.endDate)}
                      </div>
                    ) : null}
                  </div>
                  <Badge className={`${color.badge} text-xs ml-2`}>
                    {task.duration} days
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Insert New Task Dialog */}
      <Dialog open={isInsertDialogOpen} onOpenChange={setIsInsertDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Insert New Task</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="taskName" className="text-right mt-2">
                Task Name
              </Label>
              <div className="col-span-3">
                <Input 
                  id="taskName" 
                  value={newTaskName} 
                  onChange={e => handleTaskNameChange(e.target.value)} 
                  className="w-full" 
                  placeholder="Enter task name" 
                />
                {taskNameError && (
                  <div className="text-red-500 text-[13px] mt-1">
                    {taskNameError}
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="taskDuration" className="text-right mt-2">
                Duration (Days)
              </Label>
              <div className="col-span-3">
                <Input 
                  id="taskDuration" 
                  type="number" 
                  min="1" 
                  value={newTaskDuration} 
                  onChange={e => handleDurationChange(e.target.value)} 
                  className="w-full" 
                />
                {durationError && (
                  <div className="text-red-500 text-[13px] mt-1">
                    {durationError}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsInsertDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={confirmInsertTask} 
              disabled={!newTaskName.trim() || !!taskNameError || !!durationError}
            >
              Insert Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Task Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTaskIndex !== null ? tasks[selectedTaskIndex]?.name : ''}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteTask}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Task Abort Dialog */}
      <AlertDialog open={isDeleteAbortDialogOpen} onOpenChange={setIsDeleteAbortDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Task Deletion Aborted</AlertDialogTitle>
            <AlertDialogDescription>
              Task could not be deleted. A project cannot have 0 tasks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setIsDeleteAbortDialogOpen(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
